/**
 * Data layer for `clevertap-2-way-apptrove-native-sync`. Thin typed fetch wrappers
 * over /api/v1/clevertap-2-way-apptrove-native-sync. Raw `fetch()` is confined to
 * this file per the dash contract (D6).
 */

export type SyncDirection = "inbound" | "outbound" | "bidirectional";
export type SyncStatus = "active" | "paused";

export interface NativeSyncRow {
	id: string;
	tenant_id: string;
	sync_id: string;
	direction: SyncDirection;
	clevertap_account_id: string;
	clevertap_passcode: string;
	apptrove_app_token: string;
	events_synced: number;
	last_cursor: string | null;
	status: SyncStatus;
	created_at: number;
}

export interface ListNativeSyncResponse {
	items: NativeSyncRow[];
}

export interface CreateNativeSyncBody {
	sync_id: string;
	direction: SyncDirection;
	clevertap_account_id: string;
	clevertap_passcode: string;
	apptrove_app_token: string;
	last_cursor?: string | null;
}

export interface CreateNativeSyncResponse {
	ok: true;
	id: string;
	sync_id: string;
	created_at: number;
}

export interface PatchNativeSyncBody {
	status?: SyncStatus;
	last_cursor?: string | null;
	events_synced_delta?: number;
}

const BASE = "/api/v1/clevertap-2-way-apptrove-native-sync";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListNativeSyncResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListNativeSyncResponse>(res);
}

export async function create(body: CreateNativeSyncBody): Promise<CreateNativeSyncResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateNativeSyncResponse>(res);
}

export async function patch(id: string, body: PatchNativeSyncBody): Promise<{ ok: true; id: string } & Record<string, unknown>> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "PATCH",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ ok: true; id: string } & Record<string, unknown>>(res);
}

export const nativeSyncQueryKeys = {
	all: ["clevertap-2-way-apptrove-native-sync"] as const,
	list: () => [...nativeSyncQueryKeys.all] as const,
	detail: (id: string) => [...nativeSyncQueryKeys.all, id] as const,
};
